import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { changedBy, describeChange } from "@/modules/orders/changeText";
import { projectChangeRow } from "@/modules/orders/projections";
import type { SectionScreenProps } from "@/modules/types";

/**
 * Order history across every order, newest first (#59, FR-ORD-15).
 *
 * One `listChanges` call per page rather than one `history` call per order —
 * the adapter joins the order number and does the filtering and paging.
 */
export function ChangesList({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const query = useQuery({
    queryKey: ["orders", "changes", filter, sort, limit, cursor],
    queryFn: () => data.orders.listChanges({ filter, sort, limit, cursor }),
  });

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(row) => row.id}
        project={projectChangeRow}
        sort={sort}
        onToggleSort={onToggleSort}
        renderOverride={(field, row) => {
          if (field.name === "field") return describeChange(row);
          if (field.name === "actorUserId") return changedBy(row);
          return undefined;
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
